import axios, {AxiosError} from 'axios';
import window from 'global/window';
import {QueryClient, type QueryClientConfig} from '@tanstack/react-query';
import {useSessionStore} from '@netx/core-auth-next/store';
// recursive includes are happening
// import {eventProgressController} from '@netx/core-event-progress/controller';
import {dispatcher} from '../instance/dispatcher';
import {SortOrder} from '../schema/constants';
import {URL} from '../url';

// react-query client config
const queryConfig: QueryClientConfig = {
	defaultOptions: {
		queries: {
			networkMode: 'always',
			// TODO: was this the right decision - seems like we opt in here more than we use this auto opt out
			refetchOnMount: false,
			refetchOnWindowFocus: false,
			retry: false,
		},
		mutations: {
			networkMode: 'always',
			retry: false,
		},
	},
};

// react-query client instance
export const queryClient = new QueryClient(queryConfig);

/**
 * Server error (what we get back from the server)
 * @typeParam Codes - Error codes expected from the server
 */
export type ErrorResult<Codes extends number = number> = {
	code: Codes;
	data?: null;
	message: string;
	result: string;
};

/**
 * Server error response
 * @typeParam T - Error type
 * @see ErrorResult
 */
export type ErrorResponse<T extends ErrorResult = ErrorResult> = {
	error: T;
};

/**
 * Server success response
 * @typeParam T - Result type
 */
export type SuccessResponse<T> = {
	result: T;
};

/**
 * Query error
 * @typeParam Codes - Error codes expected from the server
 */
export class QueryError<Codes extends number = number> extends Error {
	code: Codes = -1 as Codes;
}

/**
 * Server paging param (`PageDTO`)
 */
export type QueryPager = {
	startIndex: number;
	size: number;
};

/**
 * Server sort param (`SortDTO`)
 */
export type QuerySort<Field extends string = string> = {
	field: Field;
	order: SortOrder;
};

interface QueryPromise<T = any> extends Promise<T> {
	cancel(): void;
}

// These are error codes that we want to handle as Cloudflare errors
const CLOUDFLARE_ERROR_CODES = [
	524, // a timeout occurred
];

// These are the error codes that we want to handle as session errors
const SESSSION_ERRORS = [6, 3001, 3002, 3003];

// typeguard to check if the error is an `AxiosError`
const isAxiosError = (error?: any): error is AxiosError =>
	error?.response !== null || error?.request !== null;

// create a query error with a message and code that we can throw
export const createQueryError = <Codes extends number = number>(
	message: string,
	code: Codes = -1 as Codes,
): QueryError<Codes> => {
	const error = new QueryError<Codes>(message);
	error.code = code;
	return error;
};

/**
 * Netx query client.\
 * Wrapper around axios to handle, session, session errors, and cancelation.
 *
 * @typeParam ErrorCodes - Error codes expected from the server
 *
 * @returns Wrapped axios client
 *
 * @example
 * const client = netxQueryClient<2102 | 1000>();
 * client.post('method', data).then((response) => {...});
 */
export const netxQueryClient = <ErrorCodes extends number = number>() => {
	const client = axios.create({
		baseURL: URL.api,
	});
	client.defaults.headers.common['Content-Type'] = 'application/json';

	const source = axios.CancelToken.source();

	// Back it up
	const _post = client.post;
	// And wrap it
	// We can ignore ts check on this, because as long as we do not modify the response
	// data, we will still be getting proper types out
	// For some reason, assigning the _post call to a var and returning is not valid but
	// just returning the _post call is valid (something in the abstract types)
	// @ts-ignore
	client.post = (method, data = [], config = {}) => {
		// every succesful sync resets the server's logout timer; reset our client one too.
		dispatcher.trigger('session:reset:logout:timer');

		if (!useSessionStore.getState().publicMethods.includes(method)) {
			(data as unknown as any[]).unshift(useSessionStore.getState().key);
		}

		const promise = _post(
			method,
			{
				id: Date.now(),
				dataContext: 'json',
				jsonrpc: '2.0',
				method,
				params: data,
			} as unknown,
			{...config, cancelToken: source.token},
		)
			.then((response) => {
				// The backend will always respond with a 200 status code and when
				// there is an error, an error object will be in the response data
				if (response.data.error as ErrorResponse) {
					const code = response.data.error.code;
					if (SESSSION_ERRORS.includes(code)) {
						// If we have a token, some calls with give sessionErrors while others will pass.
						// If not, a session error means our key is invalid.
						if (!useSessionStore.getState().isToken)
							dispatcher.trigger('session:expire');
					}
					throw createQueryError<ErrorCodes>(
						response.data.error.message,
						code as ErrorCodes,
					);
				}
				// if (method === 'getQuickEditValues') {
				// 	throw new Error('');
				// }

				dispatcher.trigger('session:reset:logout:timer');

				return response;
			})
			.catch((error) => {
				// If we have a QueryError, we can just rethrow it
				if (error instanceof QueryError) throw error;

				// If the request was cancelled, we can just rethrow it
				if (axios.isCancel(error)) throw error;

				if (isAxiosError(error)) {
					// The request was made and the server responded with a status code > 200
					if (error.response) {
						// Handle cloudflare specific errors
						if (CLOUDFLARE_ERROR_CODES.includes(error.response.status)) {
							window.Netx.eventProgressController?.notifyNetworkError({
								status: error.response.status,
								message: `Cloudflare error: ${error.response.status} - ${error.response.statusText}`,
								data: error.response.data,
								call: method,
							});
							throw createQueryError<ErrorCodes>(
								`Cloudflare error: ${error.response.status} - ${error.response.statusText}`,
								error.response.status as ErrorCodes,
							);
						}

						throw createQueryError<ErrorCodes>(
							`Server error: ${error.response.status} - ${error.response.statusText}`,
							error.response.status as ErrorCodes,
						);
					}
					// The request was made but no response was received
					if (error.request) {
						// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
						// http.ClientRequest in node.js
						window.Netx.eventProgressController?.notifyNetworkError({
							status: -1,
							call: method,
						});
						return;
					}
				}

				// we don't know what happened, just throw the error as it would have been before we
				// added this catch logic
				throw error;
			});

		// For react query to cancel on unmount (or any other to cancel via the promise)
		// TODO: soon we can remove this as axios now supports passing signal
		// just need to hunt down anything depending on this behvior and pass along the signal
		// from react query
		(promise as QueryPromise).cancel = () => {
			return source.cancel('cancelled by external script');
		};

		return promise;
	};

	return client;
};

export default netxQueryClient;
